/**
 * ⚠️  CRÍTICO — REGLA ÚNICA DE "COACH PRINCIPAL"  ⚠️
 *
 * SI VAS A LEER `coach_clients` PARA UN CLIENTE Y ELEGIR UN SOLO COACH,
 * USA ESTE HELPER. No tomes `coach_clients[0]` ni ordenes por
 * `created_at DESC` — eso da resultados erróneos cuando un cliente
 * tiene varios coaches (típico: Marco "real" + Joel "test").
 *
 * Espejos de esta regla:
 *   - Frontend TS: `getPrimaryCoachId` / `getPrimaryCoachLink` (este archivo)
 *   - Backend SQL: `public.get_primary_coach_id(client_id)`
 *   - Analytics:   `getAllClientsSetup` (analyticsBuckets.ts) — usa la
 *                  misma lógica inline porque es batch
 *
 * Si cambias la regla, actualízala en LOS TRES sitios.
 *
 * Helper único para resolver el "coach principal" de un cliente.
 *
 * Regla de negocio (única fuente de verdad — modificar AQUÍ si cambia):
 *   1. Tomar todos los coaches asignados al cliente en `coach_clients`.
 *   2. Preferir el que tenga `coach_booking_settings.is_test_account=false`.
 *   3. Si ningún coach tiene esa marca explícita o todos son test, caer
 *      al primero por orden de inserción.
 *
 * Incluye self-coach (caso Marco usa una sola cuenta cliente + coach).
 * Si quieres EXCLUIR self-coach (ej. BookTrainingCard donde nadie
 * debería poder reservar consigo mismo), filtra el resultado a mano.
 *
 * Antes de este helper, cada componente que necesitaba elegir coach
 * (CoachHubCard, useCoachInfo, RoutineSection push, BillingSection,
 * BookTrainingCard) tenía su propia lógica — y algunos elegían el más
 * reciente en vez del principal, mostrando "Joel" como coach cuando
 * el real era Marco. Centralizando aquí evitamos divergencia futura.
 */

import { supabase } from '@/integrations/supabase/client';

/** Devuelve el coach_id principal del cliente, o null si no tiene
 *  coaches asignados. */
export async function getPrimaryCoachId(clientId: string): Promise<string | null> {
  const { data: links } = await supabase
    .from('coach_clients')
    .select('coach_id')
    .eq('client_id', clientId);

  const candidateIds = (links ?? []).map(l => (l as any).coach_id as string);
  if (candidateIds.length === 0) return null;

  const { data: settings } = await supabase
    .from('coach_booking_settings')
    .select('coach_id, is_test_account')
    .in('coach_id', candidateIds);

  const testMap = new Map<string, boolean>();
  for (const s of settings ?? []) {
    testMap.set((s as any).coach_id, !!(s as any).is_test_account);
  }

  return (
    candidateIds.find(id => testMap.get(id) === false) ??
    candidateIds.find(id => id !== clientId) ??
    candidateIds[0]
  );
}

/** Igual que getPrimaryCoachId pero devuelve la fila completa de
 *  coach_clients para el coach principal, útil cuando necesitas más
 *  campos (monthly_amount, billing_day, etc.). */
export async function getPrimaryCoachLink<T extends Record<string, any> = any>(
  clientId: string,
  selectFields = '*',
): Promise<T | null> {
  const coachId = await getPrimaryCoachId(clientId);
  if (!coachId) return null;

  const { data } = await supabase
    .from('coach_clients')
    .select(selectFields)
    .eq('client_id', clientId)
    .eq('coach_id', coachId)
    .maybeSingle();

  return (data as T) ?? null;
}
